/**
 * WatermarkedPdfViewer — renders a lesson PDF piped through the backend
 * watermarking proxy so every page carries the current user's email +
 * timestamp.
 *
 * Because iframes can't send Authorization headers, we fetch the PDF as
 * a Blob and hand the iframe/anchor a `blob:` URL — that URL is torn down
 * when the component unmounts to avoid a memory leak.
 */

import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Download, ShieldCheck, AlertCircle } from 'lucide-react';
import axios from 'axios';

const WatermarkedPdfViewer = ({ pdfUrl, lessonId, title = 'PDF' }) => {
  const { API } = useContext(AuthContext);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pdfUrl) return undefined;
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/content-protection/pdf`, {
          params: { url: pdfUrl, lesson_id: lessonId },
          responseType: 'blob',
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Could not load the watermarked PDF.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [API, pdfUrl, lessonId]);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `${(title || 'lesson').replace(/[^a-z0-9-]/gi, '_').slice(0, 60)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div data-testid="watermarked-pdf-viewer">
      <div className="aspect-video w-full mb-3 relative rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-700 bg-muted/40">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            <ShieldCheck className="w-4 h-4 mr-2 animate-pulse" />
            Stamping your copy…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 text-sm text-red-600 dark:text-red-300">
            <AlertCircle className="w-6 h-6 mb-2" />
            <p className="font-bold">Could not load the protected PDF.</p>
            <p className="text-xs opacity-80 mt-1">{error}</p>
          </div>
        )}
        {blobUrl && !error && (
          <iframe
            src={blobUrl}
            title={title}
            className="w-full h-full"
            data-testid="watermarked-pdf-iframe"
          />
        )}
      </div>
      <Button
        onClick={download}
        variant="outline"
        className="w-full"
        disabled={!blobUrl}
        data-testid="watermarked-pdf-download-btn"
      >
        <Download className="w-4 h-4 mr-2" />
        Download watermarked PDF
      </Button>
      <p className="text-[11px] text-muted-foreground mt-1.5 text-center">
        Every page is stamped with your email and download timestamp.
      </p>
    </div>
  );
};

export default WatermarkedPdfViewer;
